import { useEffect } from 'react'
import useEdgeDataFetcher from './useEdgeDataFetcher'
import { useGlobalState } from './GlobalStateProvider'

/**
 * Validates the OAuth2 token response structure
 * @param {Object} data - The response data from the OAuth2 endpoint
 * @throws {Error} - If response is invalid or missing required fields
 * @returns {Object} - The validated response data
 */
const validateTokenResponse = data => {
  if (!data) {
    throw new Error('OAuth2 response is empty or null')
  }

  // Handle both standard OAuth2 format and Fyusion API format
  const accessToken = data.access_token || data.accessToken
  const tokenType = data.token_type || data.tokenType
  const expiresIn = data.expires_in || data.expiresIn

  if (!accessToken) {
    throw new Error('OAuth2 response missing access_token/accessToken field')
  }

  if (typeof accessToken !== 'string' || accessToken.trim().length === 0) {
    throw new Error('OAuth2 access_token is invalid or empty')
  }

  // Validate expires_in if present
  if (expiresIn !== undefined && (typeof expiresIn !== 'number' || expiresIn <= 0)) {
    throw new Error('OAuth2 expires_in field is invalid')
  }

  // Return normalized format
  return {
    access_token: accessToken,
    token_type: tokenType || 'Bearer',
    expires_in: expiresIn
  }
}

/**
 * Custom hook to fetch and manage Fyusion OAuth2 access token
 * Automatically refreshes token when expired (with 5 minute buffer)
 * Stores token in globalVariables for reuse across components
 */
const useFyusionFetcher = () => {
  const { globalVariables, updateGlobalVariables } = useGlobalState()
  const { fyusionAccessToken, fyusionTokenExpiry } = globalVariables.current || {}

  // Check if token is expired or about to expire (5 min buffer for safety)
  const isTokenExpired = !fyusionAccessToken || !fyusionTokenExpiry || Date.now() >= fyusionTokenExpiry - 5 * 60 * 1000

  // Only fetch token if it's expired or missing
  // Passing null to service parameter prevents the hook from making a request
  const [tokenData, isFetching] = useEdgeDataFetcher(
    isTokenExpired ? '/fyusion/oauth/token?a=b' : null,
    null, // No POST body needed
    'GET', // HTTP method
    'fyusion oauth token', // Hook name for debugging
    false, // Never cache OAuth tokens for security
    {}, // No additional headers needed
    false, // No authToken required for this endpoint
    true // Return full response to access meta info
  )
  const errors = tokenData && tokenData.meta && tokenData.meta.codes ? tokenData.meta.codes.filter(code => code !== 200000) : []

  // Store token in globalVariables when received
  useEffect(() => {
    if (tokenData && tokenData.data) {
      try {
        // Validate the token response structure
        const validatedData = validateTokenResponse(tokenData.data)
        const { access_token, expires_in, token_type } = validatedData

        // Calculate expiry timestamp
        const expiryTime = expires_in ? Date.now() + expires_in * 1000 : Date.now() + 3600 * 1000 // Default 1 hour if expires_in not provided

        updateGlobalVariables({
          fyusionAccessToken: access_token,
          fyusionTokenExpiry: expiryTime,
          fyusionTokenType: token_type || 'Bearer'
        })
        console.log('✅ Fyusion OAuth token stored successfully')
      } catch (validationError) {
        console.error('Fyusion OAuth2 token validation failed:', validationError.message)
        // Clear any existing invalid token
        updateGlobalVariables({
          fyusionAccessToken: null,
          fyusionTokenExpiry: null,
          fyusionTokenType: null
        })
      }
    }
  }, [tokenData, updateGlobalVariables])

  // Determine if we have a valid, non-expired token
  const hasValidToken = fyusionAccessToken && !isTokenExpired

  // Combine API errors with validation errorspread
  const apiError = errors && errors.length > 0 ? errors[0] : null
  const hasError = apiError || (!hasValidToken && !isFetching && tokenData)

  const isLoading = isFetching

  return {
    accessToken: fyusionAccessToken,
    isLoading,
    error: hasError ? apiError || 'Invalid token response' : null,
    isExpired: isTokenExpired
  }
}

export default useFyusionFetcher
